import {
  OccupancyType,
  PageType,
  SectionElementType,
  StudentSelectionMode,
} from '@common/entities';

import { Block, Field } from 'types/fields';
import { Section } from 'types/page';

export interface FloorPlanErrorContext {
  occupancyType?: OccupancyType;
  studentSelectionMode?: StudentSelectionMode;
}

/**
 * Return error message
 * @param {string} type
 * @param {Number} stepErrorLength
 * @param {Boolean} isWaitlistEnabled
 * @param {FloorPlanErrorContext} context - Additional context for floor plan errors
 * @returns string
 */
export function prepareError(
  type: string,
  stepErrorLength: number,
  isWaitlistEnabled: boolean,
  context?: FloorPlanErrorContext,
): string | null {
  switch (type) {
    case PageType.Blank:
    case PageType.Options:
      return `${stepErrorLength} Incomplete Fields.`;
    case PageType.FloorPlans:
      return getFloorPlanErrorMessage(isWaitlistEnabled, context);
    default:
      return null;
  }
}

function getFloorPlanErrorMessage(
  isWaitlistEnabled: boolean,
  context?: FloorPlanErrorContext,
): string {
  // If no context provided, return generic message
  if (!context) {
    return isWaitlistEnabled
      ? 'Please select a floor plan, unit, and lease term, or join a waitlist.'
      : 'Please select a floor plan, unit, and lease term.';
  }

  const { occupancyType, studentSelectionMode } = context;

  // Student bulk assignment mode - simple static message
  const isStudentBulkMode =
    occupancyType === OccupancyType.Student &&
    studentSelectionMode === StudentSelectionMode.BULK_UNIT_ASSIGNMENT;

  if (isStudentBulkMode) {
    return 'Please select a floor plan, space option, and lease term.';
  }

  // Regular mode - standard message with optional waitlist
  return isWaitlistEnabled
    ? 'Please select a floor plan, unit, and lease term, or join a waitlist.'
    : 'Please select a floor plan, unit, and lease term.';
}

export function getPageWiseFields(
  sections: Section[],
  type: PageType,
): { blockFields: string[]; singleFields: string[] } {
  const singleFields: string[] = [];
  const blockFields: string[] = [];

  if (!Array.isArray(sections) || !sections?.length) return { singleFields, blockFields };

  // Enumerate block/single fields for pages that embed section-based fields (including FloorPlans
  // when an InstallmentPlans or other block is placed on that page).
  if (![PageType.Blank, PageType.Options, PageType.FloorPlans].includes(type)) {
    return { singleFields, blockFields };
  }

  sections.forEach(({ elements }) => {
    elements?.forEach((field: Block | Field) => {
      if (field?.elementType === SectionElementType.Block) {
        blockFields.push(field.customType);
        return;
      }
      singleFields.push(field?.name);
    });
  });

  return { singleFields, blockFields };
}
